import { Avatar, Tag, theme } from 'antd'
import { MdOutlineArchive, MdOutlineBusiness, MdOutlineFolderShared, MdOutlineGroups } from 'react-icons/md'

import { classNames } from '@/Ship/Helpers/classNames'
import { initials } from '@/Ship/Helpers/fieldViews'

import { STORAGE_TYPE_LABEL, storageBadges } from '../Helpers/diskAdminFormat'

import type { DiskAdminPerson, DiskAdminStorageState, DiskAdminStorageType } from '../Contracts/DiskAdmin'
import type { ReactNode } from 'react'

import styles from './DiskAdmin.module.css'

const TYPE_ICON: Record<Exclude<DiskAdminStorageType, 'personal'>, ReactNode> = {
    company: <MdOutlineBusiness />,
    department: <MdOutlineGroups />,
    custom: <MdOutlineFolderShared />,
}

/** Аватар людини: фото, якщо бек дав `photoUrl`, інакше ініціали. */
export const PersonAvatar = ({ person, size = 32 }: { person: DiskAdminPerson; size?: number }) => {
    const { token } = theme.useToken()
    const name = person.name ?? ''
    const photo = typeof person.photoUrl === 'string' && person.photoUrl !== '' ? person.photoUrl : undefined

    return (
        <Avatar
            size={size}
            {...(photo ? { src: photo, alt: name } : {})}
            style={{
                background: token.colorPrimaryBg,
                color: token.colorPrimary,
                fontSize: Math.max(10, Math.round(size * 0.4)),
                flexShrink: 0,
            }}
        >
            {initials(name)}
        </Avatar>
    )
}

/** Значок простору без власника: компанія, відділ, довільний чи архів. */
export const SpaceIcon = ({ type, state, size = 32 }: { type: DiskAdminStorageType; state: DiskAdminStorageState; size?: number }) => {
    const { token } = theme.useToken()
    const icon = state === 'archived' ? <MdOutlineArchive /> : type === 'personal' ? <MdOutlineFolderShared /> : TYPE_ICON[type]

    return (
        <span
            className={classNames(styles['typeIcon'])}
            style={{ width: size, height: size, margin: 0, fontSize: Math.round(size * 0.55), color: token.colorTextSecondary }}
            aria-hidden
        >
            {icon}
        </span>
    )
}

/** Підпис під назвою: для людини — посада й відділи, для простору — його тип. */
export const storageSubtitle = (type: DiskAdminStorageType, owner: DiskAdminPerson | null): string => {
    if (type === 'personal' && owner) {
        const departments = (owner.departments ?? []).map((item) => item.title).filter(Boolean)

        return [owner.position, departments.join(', ')].filter(Boolean).join(' · ') || STORAGE_TYPE_LABEL.personal
    }

    return STORAGE_TYPE_LABEL[type]
}

export const StorageBadges = ({ state, departed, hasDisk }: { state: DiskAdminStorageState; departed: boolean; hasDisk: boolean }) => {
    const badges = storageBadges(state, departed, hasDisk)

    if (badges.length === 0) return null

    return (
        <span className={classNames(styles['badges'])}>
            {badges.map((badge) => (
                <Tag key={badge.label} {...(badge.tone === 'default' ? {} : { color: badge.tone })} data-badge={badge.label}>
                    {badge.label}
                </Tag>
            ))}
        </span>
    )
}

interface StorageNameProps {
    type: DiskAdminStorageType
    title: string
    state: DiskAdminStorageState
    owner: DiskAdminPerson | null
    size?: number
}

/**
 * Назва рядка диска: аватар і ПІБ співробітника (особистий диск) або значок
 * і назва простору; під нею — посада з відділами чи тип простору. Довге
 * обрізається трикрапкою, повний текст — у `title`.
 */
export const StorageName = ({ type, title, state, owner, size = 32 }: StorageNameProps) => {
    const subtitle = storageSubtitle(type, owner)

    return (
        <span className={classNames(styles['nameCell'])}>
            {type === 'personal' && owner ? (
                <PersonAvatar person={owner} size={size} />
            ) : (
                <SpaceIcon type={type} state={state} size={size} />
            )}
            <span className={classNames(styles['nameText'])}>
                <span className={classNames(styles['nameTitle'])} title={title}>
                    {title}
                </span>
                <span className={classNames(styles['nameSub'])} title={subtitle}>
                    {subtitle}
                </span>
            </span>
        </span>
    )
}
